import { useMemo } from "react";
import { useWindowRole } from "@/desktop/use-window-role";
import {
  useHasConversationFrameHistoryBeenHydrated,
  useIsConversationFrameHistoryHydrating,
} from "@/lib/conversation-frame-history-cache";
import { useProjectStore } from "@/stores/project-store";

interface ConversationFrameLoadingStateArgs {
  frameId: string;
  conversationId?: string;
  visibleMessageCount: number;
}

/**
 * Decides whether the conversation frame should render the loading
 * placeholder instead of the cold "no messages yet" empty-state.
 *
 * Returns true only when:
 *   - the frame is bound to a durable conversation,
 *   - there are no visible messages yet, and
 *   - the binding's hydration cycle has not yet settled OR is in flight.
 *
 * Owner-only: mirror windows do not run the hydration code path
 * (use-conversation-frame-history.ts is owner-gated), so a mirror would
 * read `hasBeenHydrated=false` forever and hang on the spinner for any
 * empty chat. On mirrors we return false and the cold empty-state
 * shows, matching pre-fix behavior. Mirrors will rerender once owner's
 * conversation store mirrors in.
 */
export function useConversationFrameLoadingState({
  frameId,
  conversationId,
  visibleMessageCount,
}: ConversationFrameLoadingStateArgs): { showLoadingPlaceholder: boolean } {
  const windowRole = useWindowRole();
  const currentProjectId = useProjectStore((s) => s.currentProject?.id);
  const currentSpaceId = useProjectStore((s) => s.currentSpace?.id);
  const args = useMemo(
    () => ({
      // Empty-string fallbacks produce a key that will not match any
      // real in-flight or settled key, so the hooks return false. That
      // is the correct neutral state while the workspace context is
      // still resolving.
      projectId: currentProjectId ?? "",
      spaceId: currentSpaceId ?? "",
      frameId,
      conversationId,
    }),
    [
      conversationId,
      currentProjectId,
      currentSpaceId,
      frameId,
    ],
  );
  const hydrating = useIsConversationFrameHistoryHydrating(args);
  const settled = useHasConversationFrameHistoryBeenHydrated(args);

  const isHistoryExpected = Boolean(conversationId);
  const showLoadingPlaceholder =
    windowRole === "owner" &&
    visibleMessageCount === 0 &&
    isHistoryExpected &&
    (hydrating || !settled);

  return { showLoadingPlaceholder };
}
